// Libs
import { useEffect, useMemo, useState } from "react";
import { IoIosSearch } from "react-icons/io";
// import { IoFilterOutline } from "react-icons/io5";
import { GoPlus } from "react-icons/go";
import { FiDownload } from "react-icons/fi";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useMutation } from "@tanstack/react-query";
import { FaTrash } from "react-icons/fa";
import { toast } from "sonner";
import { TbEdit } from "react-icons/tb";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { IoMdEye } from "react-icons/io";
// UI-Components
import {
  OutletHeading,
  OutletSubHeading,
} from "../../UI-Components/OutletWrapper";
import {
  DialogHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogIcon,
} from "../../UI-Components/Dialog";
import { Input } from "../../UI-Components/Input";
import { Button } from "../../UI-Components/Button";
import Spinner from "../../UI-Components/Spinner";
// Types
import {
  ActionType,
  PayerData,
  PayerFilters,
  PayersList,
} from "../../Libs/Types/Payers";
import AddNewPayer from "./AddNewPayer";
import {
  FailureResponseWrapper,
  GetPayerAPI_Response,
  GetPayerIdAPI_Response,
  SuccessResponseWrapper,
} from "../../Libs/Types/API-Responses";
// Endpoints
import {
  apiDeletePayer,
  apiGetPayerById,
  apiGetPayerList,
} from "../../Endpoints/Payers";
import { DynamicListingTable } from "../../UI-Components/DynamicListingTable";

export default function Payers() {
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isAction, setIsAction] = useState<ActionType>("View");
  const [isActionOpen, setIsActionOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPayer, setCurrentPayer] = useState<PayerData | null>(null);
  const [payerId, setPayerId] = useState<string>("");
  //   eslint-disable-next-line
  const [statusFilter, setStatusFilter] = useState<PayerFilters>("All");
  //   eslint-disable-next-line
  const [payerList, setPayerList] = useState<PayersList[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize: number = 10;
  const replaceString: string = "N/A";
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ascending" | "descending";
  } | null>(null);

  const requestSort = (key: string) => {
    let direction: "ascending" | "descending" = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // API Response Handling
  const { mutateAsync: getPayerData, isPending: isPayerDataLoading } =
    useMutation({
      mutationFn: apiGetPayerList,
      retry: 1,
      onSuccess: async (data) => {
        const respData: SuccessResponseWrapper<GetPayerAPI_Response[]> =
          data.data;
        const mappedData: PayersList[] = respData.data.map((payer) => ({
          id: payer?.payer_id ?? "",
          uniqueId: payer?.payer_code ?? replaceString,
          name: payer.payer_name ?? replaceString,
          address: JSON.parse(payer?.contact_info)?.address ?? replaceString,
          state: JSON.parse(payer?.contact_info)?.state ?? replaceString, // Fill as per real data or leave empty
          phoneNumber:
            JSON.parse(payer?.contact_info)?.phoneNumber ?? replaceString, // Fill as per real data or leave empty
          status: payer.is_active ? "Active" : "Inactive",
        }));

        setPayerList(mappedData);
      },
      onError: (error: any) => {
        const errorData: FailureResponseWrapper = error?.response?.data;
        toast.error(errorData?.message ?? "Something Went Wrong", {
          duration: 3000,
        });
      },
      throwOnError: false,
    });

  // API Response Handling
  const { mutateAsync: deletePayer, isPending: isPayerDeletePending } =
    useMutation({
      mutationFn: apiDeletePayer,
      retry: 1,
      onSuccess: async (data) => {
        const respData: SuccessResponseWrapper<GetPayerAPI_Response[]> =
          data.data;
        toast.success(respData?.message ?? "Payer Details Deleted!", {
          duration: 3000,
        });
        setPayerId("");
        getPayerData();
      },
      onError: (error: any) => {
        const errorData: FailureResponseWrapper = error?.response?.data;
        toast.error(errorData?.message ?? "Something Went Wrong", {
          duration: 3000,
        });
        handleActionClose();
      },
      throwOnError: false,
    });

  // API Response Handling
  const { mutateAsync: getPayer } = useMutation({
    mutationFn: apiGetPayerById,
    retry: 0,
    onSuccess: async (data) => {
      const respData: SuccessResponseWrapper<GetPayerIdAPI_Response> =
        data.data;
      const mappedData: PayersList = {
        id: respData.data.payer_id,
        uniqueId: respData.data.payer_code,
        name: respData.data.payer_name,
        address: JSON.parse(respData.data.contact_info)?.address,
        phoneNumber: JSON.parse(respData.data.contact_info)?.phoneNumber,
        state: JSON.parse(respData.data.contact_info)?.state,
        status: respData.data.is_active ? "Active" : "Inactive",
      };
      setCurrentPayer(mappedData);
    },
    onError: (error: any) => {
      const errorData: FailureResponseWrapper = error?.response?.data;
      toast.error(errorData?.message ?? "Something Went Wrong", {
        duration: 3000,
      });
      handleActionClose();
    },
    throwOnError: false,
  });

  // Filtered Payers
  const filteredPayers: PayersList[] = payerList.filter((payer) => {
    const matchesSearch =
      payer?.uniqueId
        ?.toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      payer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payer?.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payer?.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payer?.state?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "All" || payer.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  function getNestedValue(obj: PayersList, path: string) {
    return path.split(".").reduce((acc: any, part: any) => acc?.[part], obj);
  }

  const sortedPayers: PayersList[] = useMemo(() => {
    if (!sortConfig) return filteredPayers;

    return [...filteredPayers].sort((a, b) => {
      const aValue = String(getNestedValue(a, sortConfig.key) ?? "");
      const bValue = String(getNestedValue(b, sortConfig.key) ?? "");

      if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  }, [filteredPayers, sortConfig]);

  // Pagination calculations
  const totalPages: number = Math.ceil(sortedPayers.length / pageSize);

  // Setting Paginated Data
  const paginatedPayers: PayersList[] = sortedPayers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Handle Pagination
  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle Popup Close
  const handleDialogClose = (manageRefetch: boolean = false) => {
    if (manageRefetch) {
      getPayerData();
    }
    setDialogOpen(false);
  };
  const handleActionClose = async (manageRefetch: boolean = false) => {
    setIsActionOpen(false);
    setPayerId("");
    setCurrentPayer(null);
    if (manageRefetch) {
      await getPayerData();
    }
  };
  const handleDeleteConfirm = async () => {
    await deletePayer(payerId);
    setIsActionOpen(false);
  };
  const handleActionDialog = async (id: string, action: ActionType) => {
    setIsAction(action);
    setPayerId(id);
    if (action !== "Delete") {
      await getPayer(id);
    }
    setIsActionOpen(true);
  };
  const columns = [
    { key: "uniqueId", header: "Unique ID", sortable: true, width: "100px" },
    { key: "name", header: "Name", sortable: true, width: "100px" },
    { key: "address", header: "Address", width: "200px" },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "100px",
      cellRenderer: (row: any) => (
        <span
          className={`px-1 ${
            row.status === "Active" ? "bg-green-200" : "bg-red-200"
          }`}
        >
          {row.status}
        </span>
      ),
    },
    { key: "state", header: "State", sortable: true, width: "100px" },
    { key: "phoneNumber", header: "Phone Number", width: "120px" },
  ];

  const actions = [
    {
      name: "Edit",
      icon: <TbEdit />,
      callback: (row: any) => handleActionDialog(row.id, "Edit"),
    },
    {
      name: "Delete",
      icon: <MdOutlineDeleteOutline />,
      callback: (row: any) => handleActionDialog(row.id, "Delete"),
    },
    {
      name: "View",
      icon: <IoMdEye />,
      callback: (row: any) => handleActionDialog(row.id, "View"),
    },
  ];

  useEffect(() => {
    getPayerData();
    // eslint-disable-next-line
  }, []);
  return (
    <>
      <div className="px-2">
        <OutletHeading>Payers</OutletHeading>
        <section>
          <div className="grid grid-cols-12">
            <OutletSubHeading className="mt-2 col-span-3">
              Fill the details
            </OutletSubHeading>
            <div className="relative w-full col-span-4">
              <Input
                type="search"
                placeholder="Search ID, Name..."
                className="pr-8 border-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IoIosSearch className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground text-primary" />
            </div>
            <div className="col-span-5 px-4">
              <div className="flex gap-2 place-content-end">
                {/* <Button
                  variant={"outline"}
                  className="relative text-base font-medium pr-10"
                >
                  Filter
                  <IoFilterOutline className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                </Button> */}
                <Button
                  variant={"outline"}
                  className="relative text-base font-medium pr-10"
                >
                  Export
                  <FiDownload className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                </Button>
                <Button
                  className="relative text-base font-medium pr-10"
                  onClick={() => setDialogOpen(true)}
                >
                  New Payer
                  <GoPlus className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {isPayerDataLoading ? (
          <div className="flex justify-center items-center gap-5 mt-10">
            <span>Loading...</span>
            <Spinner className="w-6 h-6 border-primary-btn border-t-white" />
          </div>
        ) : (
          <>
            <section className="mt-4 max-h-[60vh] overflow-y-auto">
              <DynamicListingTable
                data={paginatedPayers}
                columns={columns}
                sortConfig={sortConfig}
                onSort={requestSort}
                actions={actions}
              />
            </section>
            <section>
              <div className="flex justify-end mt-2 space-x-2">
                <div className="p-2 text-disable-secondary">
                  <span className="text-primary">{currentPage}</span>
                  <span>
                    -{currentPage * pageSize} of {totalPages} Pages
                  </span>
                </div>
                <Button
                  className="p-2"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  variant="outline"
                >
                  <IoIosArrowBack />
                </Button>

                <Button
                  className="p-2"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  variant="outline"
                >
                  <IoIosArrowForward />
                </Button>
              </div>
            </section>
          </>
        )}

        <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogDescription className="hidden">
                New Payer
              </DialogDescription>
              <DialogTitle className="py-0 font-semibold">
                New Payer
              </DialogTitle>
            </DialogHeader>
            <AddNewPayer handleClosePayer={handleDialogClose} />
          </DialogContent>
        </Dialog>

        <Dialog open={isActionOpen} onOpenChange={() => handleActionClose()}>
          {isAction === "Delete" ? (
            <DialogContent className="max-w-3xl">
              <DialogIcon className="text-primary mb-0">
                <FaTrash size={50} />
              </DialogIcon>
              <DialogHeader>
                <DialogDescription className="hidden">
                  Are you sure you want to delete?
                </DialogDescription>
                <DialogTitle>Are you sure you want to delete?</DialogTitle>
              </DialogHeader>
              <div className="flex justify-between gap-2">
                <Button
                  className="w-full bg-red-200 text-red-900"
                  variant={"default"}
                  onClick={handleDeleteConfirm}
                >
                  {isPayerDeletePending ? (
                    <div className="flex justify-center items-center gap-5">
                      <Spinner className="w-6 h-6 border-red-500 border-t-white" />
                    </div>
                  ) : (
                    "Delete"
                  )}
                </Button>
                <Button
                  className="w-full"
                  variant={"cancel"}
                  onClick={() => handleActionClose()}
                >
                  Cancel
                </Button>
              </div>
            </DialogContent>
          ) : (
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogDescription className="hidden">
                  {isAction} Payer
                </DialogDescription>
                <DialogTitle className="py-0 font-semibold">
                  {isAction} Payer
                </DialogTitle>
              </DialogHeader>
              <AddNewPayer
                handleClosePayer={handleActionClose}
                setPayerValues={currentPayer!}
                isDisabled={isAction === "View" && true}
              />
            </DialogContent>
          )}
        </Dialog>
      </div>
    </>
  );
}
